import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { ArrowDown, ArrowUp } from '@/icons/ArrowUpDown';
import ChevronDown from '@/icons/ChevronDown';
import type { MembersSortBy, MembersSortDirection } from '@/types/members-list';
import { defaultSortDirection } from '@/types/members-list';

export type { MembersSortBy };

interface MembersSortControlsProps {
  sortBy: MembersSortBy;
  sortDirection: MembersSortDirection;
  onSortByChange: (sort: MembersSortBy) => void;
  onSortDirectionChange: (direction: MembersSortDirection) => void;
}

const MembersSortControls: React.FC<MembersSortControlsProps> = ({
  sortBy,
  sortDirection,
  onSortByChange,
  onSortDirectionChange
}) => {
  const { t } = useTranslation('screens');

  const sortOptions: { value: MembersSortBy; label: string }[] = [
    { value: 'last-first', label: t('filters.sortLastFirst') },
    { value: 'first', label: t('filters.sortFirst') },
    { value: 'birth', label: t('filters.sortBirth') },
    { value: 'recent', label: t('filters.sortRecent') }
  ];

  const directionLabel = sortDirection === 'asc' ? t('filters.sortAscending') : t('filters.sortDescending');

  const handleSortByChange = (nextSortBy: MembersSortBy) => {
    onSortByChange(nextSortBy);
    onSortDirectionChange(defaultSortDirection(nextSortBy));
  };

  const toggleDirection = () => {
    onSortDirectionChange(sortDirection === 'asc' ? 'desc' : 'asc');
  };

  return (
    <div className="list-search-row__sort list-sort-controls">
      <span className="list-sort-controls__heading" id="members-sort-label">
        {t('filters.sort')}
      </span>
      <div className="list-sort-controls__group">
        <Select.Root value={sortBy} onValueChange={(value) => handleSortByChange(value as MembersSortBy)}>
          <Select.Trigger className="list-sort-controls__trigger" aria-labelledby="members-sort-label">
            <Select.Value />
            <Select.Icon className="list-sort-controls__icon" aria-hidden>
              <ChevronDown width={14} height={14} />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content
              className="list-sort-controls__menu"
              position="popper"
              side="bottom"
              align="end"
              sideOffset={6}
            >
              <Select.Viewport>
                {sortOptions.map(({ value, label }) => (
                  <Select.Item key={value} value={value} className="list-sort-controls__option">
                    <Select.ItemText>{label}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
        <button
          type="button"
          className="list-sort-controls__direction"
          onClick={toggleDirection}
          title={directionLabel}
          aria-label={directionLabel}
        >
          {sortDirection === 'asc' ? <ArrowUp width={16} height={16} /> : <ArrowDown width={16} height={16} />}
        </button>
      </div>
    </div>
  );
};

export default MembersSortControls;
